// Copyright (c) 2025 Juan Beltrán
// Licensed under the MIT License. See LICENSE file for details.

import React, { useState, useRef, useEffect } from 'react';
import { CodeBlock } from './CodeBlock';
import { Copy, Check, SquarePen, Sparkles, Share2, ImageIcon } from 'lucide-react';
import { Paperclip, Send } from 'react-feather';
import { Search } from './Search';
import { formatDate } from '../utils/dateFormatter';
import { MessageContent } from './MessageContent';
import { IconButton } from './IconButton';
import type { MessageContent as MessageContentType } from '../types/models';

// Helper function to extract text content
const getTextContent = (content: MessageContentType): string => {
  if (typeof content === 'string') {
    return content;
  }
  return content.find(c => c.type === 'text')?.text || '';
};
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ThinkingBlock } from './ThinkingBlock';
import { ReasoningLoader } from './ReasoningLoader';
import { useStore } from '../store/useStore';
import { sendMessage } from '../utils/chatManager';
import { translations } from '../utils/translations';
import { LoadingDots } from './LoadingDots';
import { saveChatMessage } from '../utils/firebase';
import type { Message, FirestoreMessage, APIConfig, APIError } from '../types/models';
import type { SearchResult } from '../modules/fileHandler/types';
import { ActiveFiles } from '../modules/fileHandler/components/ActiveFiles';
import { DocumentService } from '../modules/fileHandler/services/supabase.service';
import { braveSearchService } from '../services/BraveSearchService';
import { MODELS_DATABASE } from '../config/modelsDatabase';
import { ModelSwitchModal } from './ModelSwitchModal';
import { checkImageModelSwitch } from '../utils/modelSwitcher';

// Get singleton instances
const documentService = DocumentService.getInstance();

const EnhancePromptButton: React.FC<{
  onEnhance: () => void;
  disabled: boolean;
  apiConfigs: { provider: string }[];
  language: string;
}> = ({ onEnhance, disabled, apiConfigs, language }) => {
  return (
    <div className="relative group">
      <button
        onClick={onEnhance}
        disabled={disabled}
        className={`p-2.5 rounded-xl transition-colors focus:outline-none ${
          disabled ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
        }`}
        aria-label="Enhance prompt with AI"
        title="Enhance prompt with AI"
      >
        <Sparkles className="w-5 h-5 text-gray-700 dark:text-gray-300" />
      </button>
      <div
        className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-800 text-white text-sm py-1 px-2 rounded-md pointer-events-none whitespace-nowrap"
        role="tooltip"
      >
        {apiConfigs.some(c => c.provider === 'groq') || Object.keys(import.meta.env).includes('VITE_SYSTEM_GROQ_API_KEY') 
          ? translations[language].enhancePrompt 
          : translations[language].apiNotConfigured}
      </div>
    </div>
  );
};

const ImageGenerationButton: React.FC<{
  onGenerateImage: () => void;
  disabled: boolean;
  apiConfigs: { provider: string }[];
  language: string;
}> = ({ onGenerateImage, disabled, apiConfigs, language }) => {
  return (
    <div className="relative group">
      <button
        onClick={onGenerateImage}
        disabled={disabled}
        className={`p-2.5 rounded-xl transition-colors focus:outline-none ${
          disabled ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
        }`}
        aria-label="Generate image with AI"
        title="Generate image with AI"
      >
        <ImageIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
      </button>
      <div
        className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-800 text-white text-sm py-1 px-2 rounded-md pointer-events-none whitespace-nowrap"
        role="tooltip"
      >
        {apiConfigs.some(c => c.provider === 'minimax') || Object.keys(import.meta.env).includes('VITE_SYSTEM_MINIMAX_API_KEY') 
          ? language === 'es' ? "Generar imagen" : "Generate image" 
          : translations[language].apiNotConfigured}
      </div>
    </div>
  );
};

export const Chat: React.FC = () => {
  const {
    messages,
    addMessage,
    selectedModel,
    apiConfigs,
    setError,
    error,
    clearMessages,
    currentContext,
    updateUserInfo,
    user,
    currentChatId,
    startNewChat,
    language,
    toggleConfigPanel,
    topUpValue
  } = useStore();
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [displayText, setDisplayText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [pastedImage, setPastedImage] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [searchResults, setSearchResults] = useState<Array<{ title: string; url: string; description: string }>>([]);

  const handleSearchModeChange = (active: boolean) => {
    setIsSearchMode(active);
    if (!active) {
      setSearchResults([]);
    }
  };

  const handleShare = async (content: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          text: content
        });
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          setError('Failed to share message');
        }
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, displayText]);

  useEffect(() => {
    if (input === '' && !isLoading) {
      inputRef.current?.focus();
    }
  }, [input, isLoading]);

  useEffect(() => {
    console.log('Chat component - Selected model changed:', selectedModel);
    if (selectedModel) {
      const hasUserApiKey = apiConfigs.some(config => config.provider === selectedModel.provider);
      const hasSystemApi = !!import.meta.env[`VITE_SYSTEM_${selectedModel.provider.toUpperCase()}_API_KEY`];
      const { topUpValue } = useStore.getState();

      if (!hasUserApiKey && (!hasSystemApi || topUpValue <= 0)) {
        setError('You need to add funds or configure an API key');
      } else {
        setError(null);
      }
    }
  }, [selectedModel, apiConfigs, setError]);

  useEffect(() => {
    const userInfoRegex = /my (?:name is|email is|phone is|age is) ([\w@\.\s]+)/i;
    messages.forEach(message => {
      if (message.role === 'user') {
        const content = typeof message.content === 'string'
          ? message.content
          : message.content.find(c => c.type === 'text')?.text || '';
        
        const match = content.match(userInfoRegex);
        if (match) {
          const value = match[1].trim();
          const key = match[0].toLowerCase().includes('name') ? 'name' :
                     match[0].toLowerCase().includes('email') ? 'email' :
                     match[0].toLowerCase().includes('phone') ? 'phone' :
                     match[0].toLowerCase().includes('age') ? 'age' : null;
          
          if (key && value) {
            updateUserInfo(key, value);
          }
        }
      }
    });
  }, [messages, updateUserInfo]);

  const simulateTyping = (text: string) => {
    let index = 0;
    setDisplayText('');
    
    const chunkSize = Math.max(50, Math.floor(text.length / 2));
    
    const interval = setInterval(() => {
      if (index < text.length) {
        const chunk = text.slice(index, index + chunkSize);
        setDisplayText(prev => prev + chunk);
        index += chunkSize;
      } else {
        clearInterval(interval);
        setDisplayText(text);
        inputRef.current?.focus();
      }
    }, 0);
  };

  // Auto-resize textarea when input changes
  useEffect(() => {
    const resizeTimeout = setTimeout(() => {
      inputRef.current?.style?.setProperty('height', 'auto', 'important');
      inputRef.current?.style?.setProperty(
        'height',
        `${inputRef.current?.scrollHeight}px`,
        'important'
      );
    }, 10);

    return () => clearTimeout(resizeTimeout);
  }, [input]);

  // Secure function to check if system API is available without exposing keys
  const isSystemApiAvailable = (provider: string): boolean => {
    const envVar = `VITE_SYSTEM_${provider.toUpperCase()}_API_KEY`;
    return Object.keys(import.meta.env).includes(envVar);
  };

  const handleEnhancePrompt = async () => {
    if (!input.trim() || !selectedModel || isLoading) return;

    // Check for user's Groq API first
    const groqConfig = apiConfigs.find(c => c.provider === 'groq');
    // Check for system Groq API without directly accessing the key
    const hasSystemGroqApi = isSystemApiAvailable('groq');
    
    // If neither API is available, show error
    if (!groqConfig?.apiKey && !hasSystemGroqApi) {
      setError('No Groq API configuration found. Please configure an API key or add funds to use the system API.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Security improvement: Don't use API keys directly in the client
      // Instead, use the user's API key if available or a proxy token for system API
      const apiKey = groqConfig?.apiKey || 'USE_SYSTEM_API_PROXY';
      
      // Import the enhancePrompt function
      const { enhancePrompt } = await import('../utils/providers/groq/client');
      
      // In production, this should be modified to use a proxy server for system API calls
      const response = await enhancePrompt(
        input, 
        apiKey, 
        // Pass whether this is using system API, so the client can route accordingly
        !groqConfig?.apiKey && hasSystemGroqApi
      );
      
      // Safely sanitize potential regex issues and remove quotes
const enhancedInput = response.replace(/\(\?[<P][^)]+\)/g, '(...)').replace(/^["']|["']$/g, '');
      setInput(enhancedInput);
    } catch (error) {
      // Avoid exposing raw error messages that might contain sensitive info
      const safeErrorMessage = error instanceof Error 
        ? error.message.replace(/key|api|token|secret/gi, '[REDACTED]')
        : 'Failed to enhance prompt';
      setError(safeErrorMessage);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };
  
  const handleGenerateImage = async () => {
    if (!input.trim() || !selectedModel || isLoading || isGeneratingImage) return;

    // Check for user's Minimax API first
    const minimaxConfig = apiConfigs.find(c => c.provider === 'minimax');
    // Check for system Minimax API without directly accessing the key
    const hasSystemMinimaxApi = isSystemApiAvailable('minimax');
    
    // If neither API is available, show error
    if (!minimaxConfig?.apiKey && !hasSystemMinimaxApi) {
      setError(language === 'es' 
        ? 'No se encontró configuración de API Minimax. Por favor, configure una clave API o agregue fondos para usar la API del sistema.' 
        : 'No Minimax API configuration found. Please configure an API key or add funds to use the system API.');
      return;
    }

    setIsGeneratingImage(true);
    setError(null);

    try {
      // Create new chat if none exists
      if (!currentChatId) {
        await startNewChat();
        const newChatId = useStore.getState().currentChatId;
        if (!newChatId) {
          throw new Error('Failed to create new chat');
        }
      }

      // Add user message to chat
      const promptMessage: Message = {
        id: `user-${Date.now()}`,
        content: `[Image Generation] ${input}`,
        role: 'user',
        timestamp: Date.now(),
        modelId: 'minimax-image-01',
      };
      
      addMessage(promptMessage);
      
      // Record the user message to Firestore
      if (user && currentChatId) {
        const messageData: FirestoreMessage = {
          role: promptMessage.role,
          content: getTextContent(promptMessage.content),
          timestamp: promptMessage.timestamp,
          modelId: promptMessage.modelId,
          userId: user.uid,
          userName: user.displayName || user.email || 'Anonymous'
        };
        
        await saveChatMessage(messageData, currentChatId);
      }
      
      // Use the user's API key if available, otherwise use system API
      const apiKey = minimaxConfig?.apiKey || import.meta.env.VITE_SYSTEM_MINIMAX_API_KEY;
      
      // Import the generateImage function
      const { generateImage } = await import('../utils/providers/minimax/client');
      
      // Call the image generation API
      const imageUrls = await generateImage(
        input,
        apiKey,
        user?.uid,
        topUpValue
      );
      
      setGeneratedImages(imageUrls);
      
      // Format the response with the generated images - localized
      const responseMessage: Message = {
        id: `assistant-${Date.now()}`,
        content: language === 'es' 
          ? `Imágenes generadas basadas en tu instrucción: "${input}"\n\n${
              imageUrls.map((url, index) => `![Imagen Generada ${index + 1}](${url})`).join('\n\n')
            }`
          : `Generated images based on your prompt: "${input}"\n\n${
              imageUrls.map((url, index) => `![Generated Image ${index + 1}](${url})`).join('\n\n')
            }`,
        role: 'assistant',
        timestamp: Date.now(),
        modelId: 'minimax-image-01',
      };
      
      addMessage(responseMessage);
      setInput('');
      
      // Record the assistant message to Firestore
      if (user && currentChatId) {
        const messageData: FirestoreMessage = {
          role: responseMessage.role,
          content: getTextContent(responseMessage.content),
          timestamp: responseMessage.timestamp,
          modelId: responseMessage.modelId,
          userId: user.uid,
          userName: 'AI Assistant'
        };
        
        await saveChatMessage(messageData, currentChatId);
      }
    } catch (error) {
      // Avoid exposing raw error messages that might contain sensitive info
      const errorMsg = error instanceof Error 
        ? error.message.replace(/key|api|token|secret/gi, '[REDACTED]')
        : language === 'es' ? 'Error al generar la imagen' : 'Failed to generate image';
      
      const safeErrorMessage = language === 'es'
        ? `Error: ${errorMsg}. Por favor, inténtelo de nuevo.`
        : `Error: ${errorMsg}. Please try again.`;
      
      setError(safeErrorMessage);
    } finally {
      setIsGeneratingImage(false);
      setInput('');
      inputRef.current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (items) {
      for (const item of Array.from(items)) {
        // Only allow specific image types for security
        const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        
        if (allowedImageTypes.includes(item.type)) {
          e.preventDefault();
          const file = item.getAsFile();
          
          if (file) {
            // Perform security validations similar to file upload
            
            // Validate file size (max 20MB)
            const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
            if (file.size > MAX_FILE_SIZE) {
              setError('Pasted image exceeds the maximum size limit of 20MB.');
              return;
            }
            
            // Minimum file size to prevent empty files
            const MIN_FILE_SIZE = 100; // 100 bytes as minimum for valid images
            if (file.size < MIN_FILE_SIZE) {
              setError('Pasted image appears to be invalid or empty.');
              return;
            }
            
            // Create a secure FileReader instance with timeout protection
            const reader = new FileReader();
            
            // Set timeout to prevent long-running operations
            const timeout = setTimeout(() => {
              reader.abort();
              setError('Image processing timed out. Please try a smaller or simpler image.');
            }, 5000); // 5 second timeout
            
            reader.onload = () => {
              clearTimeout(timeout);
              
              // Validate the data URL format for security
              const result = reader.result as string;
              if (typeof result === 'string' && 
                  (result.startsWith('data:image/png;base64,') || 
                   result.startsWith('data:image/jpeg;base64,') ||
                   result.startsWith('data:image/jpg;base64,'))) {
                     
                setPastedImage(result);
                setSelectedFile(file);
              } else {
                setError('Invalid image format detected.');
              }
            };
            
            reader.onerror = () => {
              clearTimeout(timeout);
              setError('Failed to process pasted image. Please try again or use the file upload option.');
            };
            
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type using both extension and MIME type for security
    const supportedExtensions = ['.txt', '.md', '.csv', '.json', '.png', '.jpg', '.jpeg'];
    const supportedMimeTypes = [
      'text/plain', 'text/markdown', 'text/csv', 'application/json', 
      'image/png', 'image/jpeg', 'image/jpg'
    ];
    
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    // Perform multiple validation checks
    if (!supportedExtensions.includes(fileExtension)) {
      setError(`Unsupported file type. Please upload one of: ${supportedExtensions.join(', ')}`);
      return;
    }
    
    // MIME type validation (more secure than just extension checking)
    if (!supportedMimeTypes.includes(file.type)) {
      setError(`Invalid file format. The file's content type (${file.type || 'unknown'}) doesn't match its extension.`);
      return;
    }
    
    // Check that extension matches MIME type
    const extensionMimeMap: Record<string, string[]> = {
      '.txt': ['text/plain'],
      '.md': ['text/markdown', 'text/plain'],
      '.csv': ['text/csv', 'text/plain'],
      '.json': ['application/json', 'text/plain'],
      '.png': ['image/png'],
      '.jpg': ['image/jpeg', 'image/jpg'],
      '.jpeg': ['image/jpeg', 'image/jpg']
    };
    
    const validMimeTypesForExtension = extensionMimeMap[fileExtension] || [];
    if (!validMimeTypesForExtension.includes(file.type)) {
      setError(`File type mismatch. The file extension doesn't match its content.`);
      return;
    }

    // Validate file size (max 20MB)
    const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
    if (file.size > MAX_FILE_SIZE) {
      setError('File size exceeds the maximum limit of 20MB.');
      return;
    }
    
    // Minimum file size check to prevent empty files
    const MIN_FILE_SIZE = 1; // 1 byte
    if (file.size < MIN_FILE_SIZE) {
      setError('File appears to be empty.');
      return;
    }

    try {
      // Create new chat if none exists
      if (!currentChatId) {
        await startNewChat();
        const newChatId = useStore.getState().currentChatId;
        if (!newChatId) {
          throw new Error('Failed to create new chat');
        }
      }

      // Check model compatibility for image files before processing
      if (file.type.startsWith('image/')) {
        const { checkImageModelSwitch, confirmModelSwitch } = await import('../utils/modelSwitcher');
        const switchResult = checkImageModelSwitch(selectedModel?.id);
        
        if (switchResult.shouldSwitch) {
          const confirmed = await confirmModelSwitch(switchResult);
          if (!confirmed) {
            setError('Please select a vision-capable model to process images');
            setSelectedFile(null);
            const fileInput = document.getElementById('fileInput') as HTMLInputElement;
            if (fileInput) {
              fileInput.value = '';
            }
            return;
          }
        }

        const reader = new FileReader();
        reader.onload = () => {
          setPastedImage(reader.result as string);
          setSelectedFile(file);
          setIsProcessingFile(false);
          setIsLoading(false);
        };
        reader.readAsDataURL(file);
        return;
      }

      // Read and process text file
      const content = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsText(file);
      });

      setIsProcessingFile(true);
      setIsLoading(true);
      setError(null);

      try {
        if (!currentChatId || !user) {
          throw new Error('No active chat or user not authenticated');
        }

  // Store the document in Supabase with chatId and userId
  const results = await documentService.storeDocument(content, {
    filename: file.name,
    processedAt: new Date().toISOString(),
    chatId: currentChatId,
    userId: user.uid
  });

  setSelectedFile(file);
  setFileContent(content);
  const chunkCount = Array.isArray(results) ? results.length : results.data?.length || 0;
  console.log(`File processed: ${chunkCount} chunks stored`);
  
  // Show success message
  const message: Message = {
    id: Date.now().toString(),
    content: `📄 File "${file.name}" processed and stored (${chunkCount} chunks). You can now ask questions about its content.`,
          role: 'assistant',
          timestamp: Date.now(),
          modelId: selectedModel?.id || '',
        };
        addMessage(message);
        
        // Trigger file list reload
        window.dispatchEvent(new Event('fileOperation'));
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
        console.error('File processing error:', error);
        setError(`Error processing file: ${errorMessage}. Please try again.`);
        setSelectedFile(null);
        setFileContent('');
      } finally {
        setIsProcessingFile(false);
        setIsLoading(false);
        // Reset file input to allow uploading the same file again
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      console.error('File processing error:', error);
      setError(`Error processing file: ${errorMessage}. Please try a smaller file or wait a moment before trying again.`);
      setInput('');
      setIsLoading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setInput('');
    setFileContent('');
    setPastedImage(null);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    // Don't auto-submit when a file is selected but there's no input
    if (!selectedModel || (!input.trim() && !selectedFile) || (selectedFile && !input.trim())) return;

    // Check model compatibility for images only if not already showing modal
    if (!isModelSwitchModalOpen && !checkAndHandleImageSubmit()) {
      return;
    }
  
    const hasUserApiKey = apiConfigs.some(config => config.provider === selectedModel.provider);
    const hasSystemApi = !!import.meta.env[`VITE_SYSTEM_${selectedModel.provider.toUpperCase()}_API_KEY`];
    const { topUpValue } = useStore.getState();

    if (!hasUserApiKey && (!hasSystemApi || topUpValue <= 0)) {
      setError('You need to add funds or configure an API key');
      return;
    }

    let effectiveModelId = selectedModel.id;
    
    // Switch to vision model ID if Grok is selected and an image is present
    if (selectedModel.provider === 'xai' && selectedModel.id === 'grok-2-latest' &&
        (pastedImage || (selectedFile && selectedFile.type.startsWith('image/')))) {
      effectiveModelId = 'grok-2-vision-latest';
    }

    const config: APIConfig = hasUserApiKey
      ? apiConfigs.find(c => c.provider === selectedModel.provider)!
      : {
          provider: selectedModel.provider,
          apiKey: import.meta.env[`VITE_SYSTEM_${selectedModel.provider.toUpperCase()}_API_KEY`],
          isSystemApi: true
        };

    if (!config) {
      setError('API configuration error');
      return;
    }

    if (!user) {
      setError('User not authenticated');
      return;
    }

    let chatId = currentChatId;
    
    if (!chatId) {
      try {
        await startNewChat();
        chatId = useStore.getState().currentChatId;
        if (!chatId) {
          setError('Failed to create new chat');
          return;
        }
      } catch (error) {
        setError('Failed to create new chat');
        return;
      }
    }
  
    setIsLoading(true);
    setError(null);
  
    try {
      // Use singleton instance for searching context

      // Search for relevant documents and chat history if there's input
      let relevantContext = '';
      let relevantChatHistory = '';
      let webSearchContext = '';
      
      if (input.trim()) {
        // Perform web search if search mode is active
        if (isSearchMode) {
          try {
            const braveResults = await braveSearchService.search(input.trim());
            if (braveResults.length > 0) {
              const searchContent = braveResults
                .map(result => `Title: ${result.title}\nSummary: ${result.description}`)
                .join('\n\n');
              
              const sourceUrls = braveResults
                .map(result => result.url)
                .join('\n');
              
              webSearchContext = `Web Search Results:\n${searchContent}\n\n` +
                'Based on the above search results, please provide a comprehensive answer to the following question. ' +
                'IMPORTANT: At the end of your response, you must include a "Sources:" section with numbered links. Use this exact format:\n\n' +
                'Sources:\n' +
                braveResults.map((result, index) =>
                  `${index + 1}. [${result.title}](${result.url})`
                ).join('\n') + '\n\n' +
                'Make sure to:\n' +
                '1. Keep the exact "Sources:" header\n' +
                '2. Preserve the numbered format (1., 2., etc.)\n' +
                '3. Use the exact Markdown link syntax shown above\n' +
                '4. Include all sources in the order provided\n\n';
            }
          } catch (error) {
            console.error('Brave search error:', error);
            setError('Failed to perform web search. Proceeding with local context only.');
          }
        }

        // Search in Supabase documents
        const searchResults = await documentService.searchDocuments(input.trim(), currentChatId);
        if (searchResults.data?.length > 0) {
          relevantContext = searchResults.data
            .map((doc: SearchResult) => `Context from ${doc.result_metadata.filename}:\n${doc.result_content}`)
            .join('\n\n');
        }

        // Search in chat history using keyword matching
        const keywords = input.toLowerCase().split(' ')
          .filter(word => word.length > 3); // Only use keywords longer than 3 chars

        const relevantMessages = messages
          .filter((msg: Message) =>
            keywords.some(keyword => getTextContent(msg.content).toLowerCase().includes(keyword))
          )
          .map((msg: Message) => `${msg.role}: ${getTextContent(msg.content)}`);

        if (relevantMessages.length > 0) {
          relevantChatHistory = relevantMessages.join('\n');
        }

        // Always include the last few messages for conversation continuity
        const recentContext = messages.slice(-3)
          .map((msg: Message) => `${msg.role}: ${getTextContent(msg.content)}`)
          .join('\n');

        relevantChatHistory = relevantChatHistory
          ? `${relevantChatHistory}\n\nRecent Context:\n${recentContext}`
          : recentContext;
      }

      let messageContent = input;
      if (selectedFile) {
        messageContent = input.trim()
          ? `Question about ${selectedFile.name}: ${input}`
          : `Please analyze this file: ${selectedFile.name}`;
      }

      // Combine all context sources
      let fullPrompt = messageContent;
      if (webSearchContext || relevantContext || relevantChatHistory) {
        const contextParts = [];
        
        if (webSearchContext) {
          contextParts.push(webSearchContext);
        }
        
        if (relevantContext) {
          contextParts.push(`Document Context:\n${relevantContext}`);
        }
        
        if (relevantChatHistory) {
          contextParts.push(`Relevant Chat History:\n${relevantChatHistory}`);
        }
        
        contextParts.push(`Question: ${messageContent}`);
        
        fullPrompt = contextParts.join('\n\n');
      }
  
      const userMessage: Message = {
        id: `user-${Date.now()}`,
        content: messageContent, // Store original message without context
        role: 'user',
        timestamp: Date.now(),
        modelId: effectiveModelId,
      };

      addMessage(userMessage);
      setInput('');
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      inputRef.current?.focus();

      const messageData: FirestoreMessage = {
        role: userMessage.role,
        content: getTextContent(userMessage.content),
        timestamp: userMessage.timestamp,
        modelId: effectiveModelId,
        userId: user.uid,
        userName: user.displayName || user.email || 'Anonymous'
      };
      
      await saveChatMessage(messageData, chatId);

      let messageToSend: string | Array<{
        type: 'text' | 'image_url';
        text?: string;
        image_url?: {
          url: string;
          detail?: 'high' | 'low';
        };
      }> = typeof fullPrompt === 'string' ? fullPrompt : getTextContent(fullPrompt);

      // If there's an image, format it according to the provider
      if (pastedImage || (selectedFile && selectedFile.type.startsWith('image/'))) {
        const imageData = pastedImage || await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(selectedFile!);
        });

        // For non-vision models, convert image to text description request
        if (!selectedModel.capabilities?.vision) {
          messageToSend = input.trim() || "What is in this image?";
        } else {
          // Format message based on provider
          if (selectedModel.provider === 'openai') {
            messageToSend = [
              {
                type: "text",
                text: input.trim() || "What is in this image?"
              },
              {
                type: "image_url",
                image_url: {
                  url: imageData
                }
              }
            ];
          } else if (selectedModel.provider === 'xai') {
            messageToSend = [
              {
                type: "image_url",
                image_url: {
                  url: imageData,
                  detail: "high"
                }
              },
              {
                type: "text",
                text: input.trim() || "What's in this image?"
              }
            ];
          } else {
            // For other providers, just send the image data
            messageToSend = imageData;
          }
        }
      }

      const response = await sendMessage(
        messageToSend,
        config,
        effectiveModelId,
        currentContext,
        messages
      );
      let formattedResponse = response;
      
      if (selectedModel?.capabilities?.reasoning) {
        // Extract thinking part and code part
        const parts = response.split(/(?=```)/);
        const thinkingPart = parts[0];
        const codeParts = parts.slice(1);

        // Show thinking part first
        simulateTyping(thinkingPart);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Brief pause after thinking

        // Then format and show code parts
        const formattedCode = codeParts.map(part => {
          return part.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, language, code) => {
            let detectedLang = language;
            if (!detectedLang) {
              if (code.includes('def ') || code.includes('import ')) {
                detectedLang = 'python';
              } else if (code.includes('function') || code.includes('const ') || code.includes('let ')) {
                detectedLang = 'javascript';
              } else if (code.includes('<?php')) {
                detectedLang = 'php';
              } else if (code.includes('<html') || code.includes('<!DOCTYPE')) {
                detectedLang = 'html';
              } else if (code.includes('@media') || code.includes('{')) {
                detectedLang = 'css';
              } else if (code.includes('SELECT ') || code.includes('CREATE TABLE')) {
                detectedLang = 'sql';
              } else if (code.includes('bash') || code.includes('$')) {
                detectedLang = 'bash';
              } else {
                detectedLang = 'text';
              }
            }
            return `\`\`\`${detectedLang}\n${code.trim()}\`\`\``;
          });
        }).join('\n\n');

        // Combine thinking part and formatted code
        formattedResponse = thinkingPart + '\n\n' + formattedCode;
        simulateTyping(formattedCode);
      } else {
        simulateTyping(response);
      }

      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        content: formattedResponse,
        role: 'assistant',
        timestamp: Date.now(),
        modelId: effectiveModelId,
      };

      addMessage(assistantMessage);

      // Save assistant message to Firestore
      await saveChatMessage({
        role: assistantMessage.role,
        content: getTextContent(assistantMessage.content),
        timestamp: assistantMessage.timestamp,
        modelId: assistantMessage.modelId,
        userId: user.uid,
        userName: 'AI Assistant'
      }, chatId);
    } catch (error) {
      if (error && typeof error === 'object' && 'code' in error) {
        const apiError = error as APIError;
        const errorMessage = apiError.message || '';
        
        // Extract balance and cost from error message if available
        const balanceMatch = errorMessage.match(/\$(\d+\.\d+)/);
        const costMatch = errorMessage.match(/cost: \$(\d+\.\d+)/);
        
        switch (apiError.code) {
          case 'insufficient_funds':
            const translatedMessage = translations[language].insufficientFunds
              .replace('{{balance}}', balanceMatch?.[1] || '0')
              .replace('{{cost}}', costMatch?.[1] || '0');
            setError(translatedMessage);
            break;
          case 'invalid_api_key':
            setError(translations[language].invalidApiKey);
            break;
          case 'api_not_configured':
            setError(translations[language].apiNotConfigured);
            break;
          default:
            setError(apiError.message || 'Failed to get response from AI');
        }
      } else {
        setError(error instanceof Error ? error.message : 'Failed to get response from AI');
      }
      inputRef.current?.focus();
    } finally {
      setIsLoading(false);
      setSelectedFile(null);
      setFileContent('');
      inputRef.current?.focus();
    }
  };
    
  const [isModelSwitchModalOpen, setIsModelSwitchModalOpen] = useState(false);
  const [modelSwitchMessage, setModelSwitchMessage] = useState('');
  const [targetModelId, setTargetModelId] = useState('');

  const handleModelSwitch = async (confirmed: boolean) => {
    setIsModelSwitchModalOpen(false);
    if (confirmed && targetModelId) {
      const targetModel = MODELS_DATABASE[targetModelId];
      if (targetModel) {
        const { apiConfigs } = useStore.getState();
        const aiModel = {
          ...targetModel,
          isConfigured: apiConfigs.some(config => config.provider === targetModel.provider),
          systemApiCost: targetModel.pricing.prompt_tokens_per_1k * 1000
        };
        useStore.getState().selectModel(aiModel);
        // Small delay to ensure model state is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        handleSubmit();
      }
    }
  };

  const checkAndHandleImageSubmit = () => {
    if (pastedImage || (selectedFile && selectedFile.type.startsWith('image/'))) {
      if (!selectedModel?.capabilities?.vision) {
        const switchResult = checkImageModelSwitch(selectedModel?.id);
        if (switchResult.shouldSwitch) {
          setModelSwitchMessage(switchResult.reason);
          setTargetModelId(switchResult.targetModel);
          setIsModelSwitchModalOpen(true);
          return false;
        }
      }
    }
    return true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (checkAndHandleImageSubmit()) {
        handleSubmit();
      }
    }
  };

  const handleCopy = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Update copied state immediately
      setCopiedMessageId(messageId);
      
      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
      
    } catch (error) {
      console.error('Failed to copy message:', error);
      // If copy fails, ensure copied state is cleared
      setCopiedMessageId(null);
    }
  };

  console.log('Rendering messages:', messages);
  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Main chat area with messages */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 bg-white dark:bg-gray-800">
        {messages.length === 0 && !input.trim() && !isProcessingFile ? (
          <div key="welcome-container" className="h-full flex items-center justify-center">
            <div key="welcome-content" className="text-center max-w-md px-6 py-8 bg-white dark:bg-gray-750 rounded-2xl shadow-sm">
              <img
                src="/icons/camelon-negro-logo-transparente.png"
                alt="Camelon AI"
                width={120}
                height={120}
                className="mx-auto h-auto"
                loading="eager"
              />
              <h2 className="text-2xl font-bold mt-6 text-gray-800 dark:text-gray-200">
                {translations[language].welcomeMessage}
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-400 text-sm">
                Select a model and start your conversation.
              </p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              } ${index === messages.length - 1 ? 'animate-fadeIn' : ''}`}
            >
              <MessageContent
                message={message}
                copiedMessageId={copiedMessageId}
                handleCopy={handleCopy}
                handleShare={handleShare}
              />
            </div>
          ))
        )}
        
        {(isLoading || isGeneratingImage) && (
          <div className="flex flex-col items-start gap-4 animate-pulse">
            {selectedModel?.capabilities?.reasoning && !isGeneratingImage ? (
              <ReasoningLoader />
            ) : (
              <div key="loading-container" className="flex justify-center mt-4 w-full">
                <div key="loading-dots-container" className="bg-white dark:bg-gray-750 px-6 py-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                  <LoadingDots
                    text={
                      isProcessingFile ? (language === 'es' ? 'Procesando archivo...' : 'Processing file...') : 
                      isGeneratingImage ? (language === 'es' ? 'Generando imágenes, por favor espere...' : 'Generating images, please be patient...') : 
                      undefined
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
        
        <div ref={messagesEndRef} />
        
        {error && (
          <div key="error-container" className="flex justify-center animate-slideUp">
            <div key="error-message" className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 px-5 py-3 rounded-lg text-sm border border-red-100 dark:border-red-800/50 shadow-sm">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active files section */}
      <div className="bg-gray-50 dark:bg-gray-750">
        <ActiveFiles
          chatId={currentChatId}
          onFileDelete={async (filename) => {
            try {
                setError(null);
                
                if (!currentChatId) {
                    throw new Error('No active chat');
                }

                if (!user) {
                    throw new Error('User not authenticated');
                }

                console.log(`Attempting to delete file: ${filename} from chat: ${currentChatId}`);
                const result = await documentService.deleteDocumentsByFilename(filename, currentChatId, user.uid);
                console.log('File deletion completed, rows deleted:', result);
              
              // Reset file states
              setSelectedFile(null);
              setFileContent('');
              
              // Reset file input
              const fileInput = document.getElementById('fileInput') as HTMLInputElement;
              if (fileInput) {
                fileInput.value = '';
              }

              // Trigger file list reload after successful deletion
              window.dispatchEvent(new Event('fileOperation'));
            } catch (error) {
              console.error('Error deleting file:', error);
              setError(error instanceof Error ? error.message : 'Failed to delete file');
              throw error;
            }
          }}
        />
      </div>

      {/* Input form section */}
      <form
        onSubmit={handleSubmit}
        className="p-4 bg-gray-100 dark:bg-gray-700 shadow-md"
      >
        <div className="flex flex-col md:flex-row md:items-start md:space-x-3 space-y-4 md:space-y-0">
          <div className="relative flex-1">
            <div className="relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={selectedModel ? translations[language].typeMessage : translations[language].selectModelToStart}
                className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 
                  transition-all duration-200 min-h-[60px] dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 
                  dark:placeholder-gray-400 shadow-sm"
                disabled={!selectedModel || isLoading || !user}
                rows={1}
                readOnly={false}
                style={{ height: 'auto', maxHeight: '33vh', overflowY: 'auto' }}
                onInput={() => {
                  if (inputRef.current) {
                    inputRef.current.style.height = 'auto';
                    inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
                  }
                }}
              />
              {pastedImage && (
                <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-center">
                    <img
                      src={pastedImage}
                      alt="Pasted image"
                      className="w-full max-w-[100px] sm:max-w-xs h-auto max-h-[100px] object-contain rounded"
                    />
                    <button 
                      onClick={handleRemoveFile} 
                      className="ml-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-center space-x-2 md:space-x-3">
            <input
              type="file"
              id="fileInput"
              onChange={handleFileSelect}
              className="hidden"
              disabled={!selectedModel || isLoading}
              accept=".txt,.md,.csv,.json,.png,.jpg,.jpeg"
            />
            <div className="relative group">
              <IconButton
                icon={<Paperclip size={20} />}
                onClick={() => document.getElementById('fileInput')?.click()}
                ariaLabel="Attach file"
                className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 
                  transition-colors duration-200 text-gray-600 dark:text-gray-300"
              />
              <div className="absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 opacity-0 group-hover:opacity-100 
                transition-opacity duration-200 bg-gray-800 text-white text-sm py-1 px-2 rounded-md pointer-events-none 
                whitespace-nowrap z-10">
                {!selectedModel
                  ? translations[language].selectModelToStart
                  : !user
                    ? translations[language].login
                    : translations[language].uploadTextFiles }
              </div>
            </div>
            
            <EnhancePromptButton
              onEnhance={handleEnhancePrompt}
              disabled={!selectedModel || isLoading || (!!selectedFile && selectedModel.provider !== 'groq') || !input.trim() || (!apiConfigs.some(c => c.provider === 'groq') && !import.meta.env.VITE_SYSTEM_GROQ_API_KEY)}
              apiConfigs={apiConfigs}
              language={language}
            />
            
            <ImageGenerationButton
              onGenerateImage={handleGenerateImage}
              disabled={!selectedModel || isLoading || isGeneratingImage || !input.trim() || (!apiConfigs.some(c => c.provider === 'minimax') && !import.meta.env.VITE_SYSTEM_MINIMAX_API_KEY)}
              apiConfigs={apiConfigs}
              language={language}
            />
            
            <Search
              onSearchModeChange={handleSearchModeChange}
              disabled={!selectedModel || isLoading}
            />
            
            <IconButton
              icon={<Send size={20} />}
              onClick={handleSubmit}
              ariaLabel="Send message"
              className={`p-2.5 rounded-xl transition-colors duration-200 ${
                !selectedModel || isLoading
                  ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-gray-800 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 shadow-sm'
              } text-white`}
              disabled={!selectedModel || isLoading}
            />
          </div>
        </div>
      </form>
      
      <ModelSwitchModal
        isOpen={isModelSwitchModalOpen}
        onConfirm={() => handleModelSwitch(true)}
        onCancel={() => handleModelSwitch(false)}
        language={language}
        message={modelSwitchMessage}
      />
    </div>
  );
};